<template>
  <div class="flex flex-col" v-if="invoice">
    <div class="page-header">
      <div class="flex items-center w-full mobile:grid mobile:grid-cols-4">
        <div class="mr-3 cursor-pointer">
          <button class="btn-danger" @click="closeDetailView">Tagasi</button>
        </div>
        <div class="title mobile:col-span-3 mobile:justify-end">
          Arve nr {{ invoice.invoice_nr }}
        </div>
        <div class="ml-auto mobile:col-span-4">
          <router-link
            v-if="invoice.project"
            :to="`/projects/${invoice.project.uuid}`"
            target="_blank"
          >
            {{ invoice.project.title }} /
          </router-link>
          <span>
            {{
              `${
                invoice.added_by ? invoice.added_by.display_username : "-"
              } / ${
                invoice.created_at
                  ? moment(invoice.created_at).format("DD.MM.YYYY")
                  : "-"
              }`
            }}
          </span>
        </div>
      </div>
    </div>
    <div class="flex mb-4 gap-x-4 mobile:flex-col">
      <div class="data-container w-2/3 mb-auto mobile:w-full">
        <div class="data-container-header mobile:flex-col">
          <h3 class="flex mobile:justify-start mobile:w-full">Üldinfo</h3>
          <div class="flex ml-auto items-center">
            <button class="btn-primary mr-3" @click="copyInvoice(false)">
              Kopeeri arvet
            </button>
            <button class="btn-primary mr-3" @click="copyInvoice(true)">
              Kopeeri arvet ning tulusid
            </button>
            <button class="btn-primary" @click="createCreditInvoice">
              Koosta kreeditarve
            </button>
          </div>
        </div>
        <div class="data-container-content data-container-content-hor">
          <div class="flex flex-col w-full pr-2 mobile:pr-0 mobile:w-full">
            <div class="invoice-form-row grid-cols-4 gap-x-2">
              <div class="title col-span-2">
                <gwd-formlabel title="Arve klient" />
              </div>
              <div class="title">
                <gwd-formlabel title="Registrikood" />
              </div>
              <div class="title">
                <gwd-formlabel title="KMKR kood" />
              </div>
              <div class="content col-span-2">
                <router-link
                  :to="`/clients/${invoice.client.id}`"
                  target="_blank"
                >
                  {{
                    invoice.client.name === invoice.client.invoice_name
                      ? invoice.client.name
                      : `${invoice.client.name} (vana nimi ${invoice.client.invoice_name})`
                  }}
                </router-link>
              </div>
              <div class="content">
                <span> {{ invoice.client.register_code }}</span>
              </div>
              <div class="content">
                <span>
                  {{
                    invoice.client.tax_code ? invoice.client.tax_code : "-"
                  }}</span
                >
              </div>
            </div>
            <div
              class="invoice-form-row grid-cols-4 gap-x-2"
              v-if="invoice.payer"
            >
              <div class="title col-span-2">
                <gwd-formlabel title="Arve maksja" />
              </div>
              <div class="title">
                <gwd-formlabel title="Registrikood" />
              </div>
              <div class="title">
                <gwd-formlabel title="KMKR kood" />
              </div>
              <div class="content col-span-2">
                <router-link
                  :to="`/clients/${invoice.payer.id}`"
                  target="_blank"
                >
                  {{
                    invoice.payer.name === invoice.payer.invoice_name
                      ? invoice.payer.name
                      : `${invoice.payer.name} (vana nimi ${invoice.payer.invoice_name})`
                  }}
                </router-link>
              </div>
              <div class="content">
                <span>{{ invoice.payer.register_code }}</span>
              </div>
              <div class="content">
                <span>{{ invoice.payer.tax_code }}</span>
              </div>
            </div>
            <div
              class="invoice-form-row gap-x-2"
              :class="invoice.payer ? 'grid-cols-2' : ''"
            >
              <div class="title">
                <gwd-formlabel
                  :title="!invoice.payer ? 'Aadress' : 'Kliendi aadress'"
                />
              </div>
              <div v-if="invoice.payer" class="title">
                <gwd-formlabel title="Maksja aadress" />
              </div>
              <div class="content">
                <span>
                  {{
                    invoice.client.location
                      ? invoice.client.location.long_address
                      : "-"
                  }}
                </span>
              </div>
              <div v-if="invoice.payer" class="content">
                {{
                  invoice.payer.location
                    ? invoice.payer.location.long_address
                    : "-"
                }}
              </div>
            </div>
            <div
              class="invoice-form-row grid-cols-4 gap-x-2 mobile:grid-cols-2 mobile:gap-y-1"
            >
              <div class="title">
                <gwd-formlabel title="Summa (-km)" />
              </div>
              <div class="title">
                <gwd-formlabel title="Käibemaks" />
              </div>
              <div class="title">
                <gwd-formlabel title="Summa kokku" />
              </div>
              <div class="title">
                <gwd-formlabel title="Laekumata summa" />
              </div>
              <div class="content">
                <span>{{
                  `${round2(invoice.no_tax_sum)}${
                    invoice.currency && currency ? currency.sign : "€"
                  }`
                }}</span>
              </div>
              <div class="content flex-col">
                <span>{{
                  `${round2(invoice.tax_sum)}${
                    invoice.currency && currency ? currency.sign : "€"
                  }`
                }}</span>
              </div>
              <div class="content">
                <span class="flex-grow font-bold">
                  {{
                    `${round2(invoice.total_sum)}${
                      invoice.currency && currency ? currency.sign : "€"
                    }`
                  }}
                </span>
              </div>
              <div
                class="content"
                :class="{
                  'text-danger':
                    moment().isAfter(moment(invoice.due_date), 'day') &&
                    !invoice.paid,
                }"
              >
                <span class="flex-grow font-bold">
                  {{
                    `${invoiceUnpaidAmount}${
                      invoice.currency && currency ? currency.sign : "€"
                    }`
                  }}
                </span>
              </div>
              <div
                class="content"
                v-if="invoice.currency && currency && currency.code !== 'EUR'"
              >
                {{ convertedListSum }}€
              </div>
              <div
                class="content"
                v-if="invoice.currency && currency.code !== 'EUR'"
              >
                {{ convertedTaxSum }}€
              </div>
              <div
                class="content"
                v-if="invoice.currency && currency && currency.code !== 'EUR'"
              >
                {{ convertedTotalSum }}€
              </div>
              <div
                class="content"
                v-if="invoice.currency && currency && currency.code !== 'EUR'"
              >
                {{ convertedUnpaidSum }}€
              </div>
            </div>
            <div class="invoice-form-row grid-cols-4 gap-x-2">
              <div class="title col-span-2">
                <gwd-formlabel title="Viitenumber" />
              </div>
              <div class="title">
                <gwd-formlabel title="Arve tüüp" />
              </div>
              <div class="title">
                <gwd-formlabel v-if="false" title="Kurss" :required="false" />
              </div>
              <div class="content items-center col-span-2">
                <span v-if="invoice.reference_number">
                  {{ invoice.reference_number }}
                </span>
                <span v-else>-</span>
              </div>

              <div
                class="content"
                :class="{ 'cursor-pointer': invoice.type === 4 }"
                @click.exact.left="routeToCreditInvoiceNewTab"
                @click.exact.ctrl="routeToCreditInvoiceNewTab"
                @click.exact.middle="routeToCreditInvoiceNewTab"
              >
                {{
                  [
                    "Ülekanne",
                    "Sularaha",
                    "Kaardimakse",
                    `Kreeditarve arvele ${invoice.credit_invoice_source}`,
                  ][invoice.type - 1]
                }}
              </div>
              <div class="content">
                <span v-if="false"
                  >{{ invoice.currency_rate }} {{ invoice.currency }} = 1 EUR
                </span>
              </div>
            </div>
            <div
              class="invoice-form-row grid-cols-4 gap-x-2 mobile:grid-cols-2 mobile:gap-y-1"
            >
              <div class="title">
                <gwd-formlabel title="Käibemaksu kuupäev" />
              </div>
              <div class="title">
                <gwd-formlabel title="Arve kuupäev" />
              </div>
              <div class="title">
                <gwd-formlabel title="Maksetähtaeg (päeva)" />
              </div>
              <div class="title">
                <gwd-formlabel title="Maksetähtpäev" />
              </div>
              <div class="content">
                <span>{{
                  moment(invoice.received_date).format("DD.MM.YYYY")
                }}</span>
              </div>
              <div class="content">
                <span>{{
                  moment(invoice.invoice_date).format("DD.MM.YYYY")
                }}</span>
              </div>
              <div class="content">
                <span>{{ invoice.payment_term }}</span>
              </div>
              <div class="content">
                <span>{{ moment(invoice.due_date).format("DD.MM.YYYY") }}</span>
              </div>
            </div>
            <div class="invoice-form-row">
              <div class="title">
                <gwd-formlabel title="Arve kirjeldus" />
              </div>
              <div class="content">
                <span class="whitespace-pre-wrap">
                  {{ invoice.invoice_content_text }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="data-container-header"><h3>Arve read</h3></div>
        <div class="flex flex-col items-start text-sm mb-auto">
          <revenue-with-costs-list
            :revenues="invoiceRevenues"
            :currency="currency"
            :currency-rate="currencyRate"
            ref="costList"
          />
        </div>
      </div>
      <div class="data-container w-1/3 mobile:w-full">
        <div class="data-container-header">
          <h3>Arve saatmise info</h3>
        </div>
        <div class="data-container-content max-w-full">
          <div
            v-for="sendingData in invoice.sending_datas"
            :key="sendingData.id"
            class="flex flex-col mb-4 max-w-full p-1"
            :class="{ 'bg-danger/25': sendingData.email_sending_error }"
          >
            <div class="flex flex-1 w-full mobile:flex-col">
              <div class="invoice-form-row grid-cols-2 flex-grow mobile:w-full">
                <div class="title">
                  <gwd-formlabel title="Arve saatja" />
                </div>
                <div class="title">
                  <gwd-formlabel title="Saatmise aeg" />
                </div>
                <div class="content">
                  {{ sendingData.confirm_profile.display_username }}
                </div>
                <div class="content">
                  {{
                    moment(sendingData.created_at).format("DD.MM.YYYY HH:mm")
                  }}
                </div>
              </div>
            </div>
            <div
              v-if="sendingData.email_sending_error"
              class="flex flex-1 w-full mobile:flex-col"
            >
              <span class="text-danger text-lg"
                >E-arve saadetud, meili saatmine ebaõnnestus!</span
              >
            </div>
            <div
              class="grid grid-cols-2 border-t border-offwhite-dark items-center py-2 mobile:flex-col mobile:grid-cols-1 mobile:gap-y-1"
            >
              <div class="flex items-center gap-x-2">
                <h4>PDF</h4>
                <div class="content">
                  <div class="file-display read-only">
                    <span
                      class="export-button"
                      @click="exportFile(sendingData, true)"
                    >
                      {{
                        sendingData.pdf_file.split("/")[
                          sendingData.pdf_file.split("/").length - 1
                        ]
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-x-2">
                <h4>XML</h4>
                <div class="content">
                  <div class="file-display read-only">
                    <span
                      v-if="sendingData.xml_file"
                      class="export-button"
                      @click="exportFile(sendingData, false)"
                    >
                      {{
                        sendingData.xml_file.split("/")[
                          sendingData.xml_file.split("/").length - 1
                        ]
                      }}
                    </span>
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="grid grid-cols-3 border-t border-offwhite-dark items-center py-2 mobile:flex-col mobile:grid-cols-1 mobile:gap-y-1"
            >
              <div class="flex items-center">
                <h4 class="mr-2">Meil</h4>
                <span
                  class="typcn rounded-full h-5 w-5 flex text-white justify-center items-center leading-none"
                  :class="
                    sendingData.email_sent
                      ? 'typcn-tick bg-primary'
                      : 'typcn-times bg-danger'
                  "
                />
              </div>
              <div class="flex items-center">
                <h4 class="mr-2">E-arve</h4>
                <span
                  class="typcn rounded-full h-5 w-5 flex text-white justify-center items-center leading-none"
                  :class="
                    sendingData.e_invoice_sent
                      ? 'typcn-tick bg-primary'
                      : 'typcn-times bg-danger'
                  "
                />
              </div>
              <div class="flex items-center">
                <h4 class="mr-2">E-arve ID</h4>
                <span>
                  {{
                    sendingData.receiver_invoice_id
                      ? sendingData.receiver_invoice_id
                      : "-"
                  }}</span
                >
              </div>
            </div>
            <div
              class="flex flex-col max-w-full w-full border-t border-offwhite-dark gap-y-2"
              v-if="sendingData.email_sent"
            >
              <h4 class="flex">{{ sendingData.email_title }}</h4>
              <div
                class="flex max-w-full mobile:w-full mobile:flex-col mobile:gap-y-1"
              >
                <div
                  v-for="email in sendingData.email_addresses.split(';')"
                  :key="email"
                  class="flex items-center gap-x-1"
                >
                  <div
                    class="flex h-5 w-5 bg-primary justify-center items-center leading-none text-white rounded-full"
                  >
                    <span class="typcn typcn-user" />
                  </div>

                  <span>{{ email }}</span>
                </div>
              </div>
              <div class="flex flex-1 max-w-full w-full">
                <span
                  class="whitespace-pre-wrap max-w-full flex break-words mobile:w-full"
                  :style="{ hyphens: 'auto' }"
                >
                  {{ sendingData.email_body }}
                </span>
              </div>
            </div>

            <div
              class="flex items-center gap-x-3"
              v-if="sendingData.email_sent || sendingData.e_invoice_sent"
            >
              <button
                class="btn-primary"
                @click="toggleEmailSending(sendingData, false)"
              >
                Saada uus meil
              </button>
              <button
                class="btn-primary"
                @click="toggleEmailSending(sendingData, true)"
              >
                <span class="label">Saada kordusarve</span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="invoice.bank_transfers && invoice.bank_transfers.length > 0"
          class="data-container-header"
        >
          <h3>Seotud laekumised</h3>
        </div>
        <div class="data-container-content gap-y-2">
          <span
            class="px-1 py-0.5 cursor-pointer inline-block rounded font-medium bg-green"
            v-for="transfer_data in invoice.bank_transfers"
            :key="transfer_data.transfer.id"
            @click.left.exact="routeToBankTransfer(transfer_data.transfer)"
            @click.middle.exact="routeToBankTransfer(transfer_data.transfer)"
            @click.ctrl.exact="routeToBankTransfer(transfer_data.transfer)"
          >
            Laekumine
            {{ transfer_data.transfer.queue_number }}
            ( {{ transfer_data.sum }} €)
          </span>
        </div>
      </div>
    </div>
    <email-sending-modal
      v-if="newEmailSending"
      :sending-info="newEmailSendingData"
      :resend="newEmailSendingRepeatEmail"
      :invoice-loading="invoiceLoading"
      @sendNewEmail="sendNewEmail"
      @closeModal="
        () => {
          this.newEmailSending = false;
          this.newEmailSendingData = null;
          this.newEmailSendingRepeatEmail = false;
        }
      "
    />
    <div
      class="flex gap-x-2 mobile:flex-wrap mobile:gap-y-1 mobile:mx-2 mobile:mb-2"
    >
      <button
        class="btn-primary"
        v-if="invoice.project"
        @click="$router.push(`/projects/${invoice.project.uuid}`)"
      >
        <span class="label">Tagasi tellimuse juurde</span>
      </button>
      <button
        class="btn-primary"
        v-if="invoice.client"
        @click="$router.push(`/clients/${invoice.client.id}/?page=2`)"
      >
        <span class="label">Tagasi kliendi tellimuste juurde</span>
      </button>
      <button
        v-if="invoice.project && allowFinished"
        class="confirm-button relative"
        :class="allowFinished.can_finish ? 'btn-turquoise' : 'btn-disabled'"
        @click="allowFinished.can_finish ? markProjectAsCompleted() : ''"
      >
        <span class="label">Märgi lõpetatuks</span>
        <ul v-if="!allowFinished.can_finish" class="confirm-checklist">
          <li v-if="!allowFinished.costs_on_invoice">Kõik kulud pole arvel</li>
          <li v-if="!allowFinished.invoice_sent">Kõik arved pole saadetud</li>
        </ul>
      </button>
      <button
        v-if="accountingPermissions === 'R1'"
        class="btn-danger"
        @click="$router.push(`/invoices/outgoing/${invoice.id}`)"
      >
        Muuda arvet
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { convertCurrency, round2 } from "@/assets/utils/commonMath";
import { saveAs } from "file-saver";
import { Revenue } from "@/assets/types/Revenue";
import EmailSendingModal from "@/components/invoices/EmailSendingModal.vue";
import RevenueWithCostsList from "@/components/invoices/RevenueWithCostsList.vue";

export default {
  name: "SentOutgoingInvoice",
  components: { RevenueWithCostsList, EmailSendingModal },
  data() {
    return {
      returnUrl: null,
      invoice: null,
      moment: moment,
      currencyRate: null,
      invoiceRevenues: [],
      newEmailSending: false,
      newEmailSendingData: null,
      newEmailSendingRepeatEmail: false,
      allowFinished: false,
      invoiceLoading: false,
    };
  },
  methods: {
    round2,
    convertCurrency,
    routeToCreditInvoiceNewTab() {
      if (this.invoice.type !== 4) return;
      const routeData = this.$router.resolve({
        path: `/invoices/outgoing/confirmed/${this.invoice.credit_invoice_id}/`,
      });
      window.open(routeData.href, "_blank");
    },
    routeToBankTransfer(transfer) {
      const routeData = this.$router.resolve({
        path: `/transfer/${transfer.id}/`,
      });
      window.open(routeData.href, "_blank");
    },
    checkProjectCanBeCompleted() {
      if (!this.invoice.project) return;
      this.apiRequest(
        `costs/${this.companyId}/project/${this.invoice.project.uuid}/finished-check/`,
        "get",
        true
      ).then((res) => {
        this.allowFinished = res.data;
      });
    },
    markProjectAsCompleted() {
      if (!this.invoice.project) return;

      this.apiRequest(
        `company/draft/${this.invoice.project.uuid}/edit/`,
        "patch",
        true,
        { status: 5 }
      ).then((res) => {
        if (res.status === 200) {
          this.allowFinished = null;
          this.retrieveInvoice();
        }
      });
    },
    sendNewEmail(data) {
      this.invoiceLoading = true;
      this.apiRequest(
        `invoice/${this.companyId}/outgoing/${this.$route.params.invoiceId}/info/${this.newEmailSendingData.id}/resend/`,
        "post",
        true,
        data
      )
        .then((res) => {
          if (res.status === 201) {
            this.newEmailSendingData = null;
            this.newEmailSending = false;
            this.invoiceLoading = false;
            this.retrieveInvoice();
          }
        })
        .catch((err) => {
          this.invoiceLoading = false;
          const errorCodes = [
            {
              code: 32,
              text: "Valitud maksjale ei saa saata E-arveid.",
            },
            {
              code: 34,
              text: "Valitud kliendile ei saa saata E-arveid.",
            },
            {
              code: 36,
              text: "Viga e-arve saatmisega.",
            },
          ];
          const errorMessage = errorCodes.find(
            (x) => x.code === err.response.data.code
          );

          this.$store.dispatch("messageHandler/throwMessage", {
            type: "error",
            error: err,
            text: errorMessage ? errorMessage.text : "Midagi läks valesti.",
          });
        });
    },
    toggleEmailSending(e, repeatEmail = false) {
      this.newEmailSendingData = e;
      this.newEmailSendingRepeatEmail = repeatEmail;
      this.newEmailSending = true;
    },
    retrieveInvoice() {
      this.apiRequest(
        `invoice/${this.companyId}/outgoing/${this.$route.params.invoiceId}/?view=sent`,
        "get",
        true
      ).then((res) => {
        this.invoice = JSON.parse(JSON.stringify(res.data));
        document.title = `Müügiarve ${res.data.invoice_nr} | ${this.config.title}`;
        this.currencyRate = this.invoice.currency_rate;
        this.loadInvoiceRevenuesWithCosts();
        if (this.invoice.project && this.invoice.project.status < 5) {
          this.checkProjectCanBeCompleted();
        }
      });
    },
    loadInvoiceRevenuesWithCosts() {
      this.apiRequest(
        `invoice/${this.companyId}/outgoing/${this.$route.params.invoiceId}/costs/`,
        "get",
        true
      ).then((res) => {
        this.invoiceRevenues = res.data.map((revenueData) => {
          const x = revenueData.revenue;
          const revenue = new Revenue(
            x.id,
            this.serviceObjects.find(
              (y) => parseInt(y.service_code) === parseInt(x.accounting_code)
            ),
            x.amount,
            x.unit,
            this.taxTypes.find((y) => parseInt(y.id) === x.vat_id),
            x.price,
            null,
            x.name
          );
          revenue.costs = revenueData.costs;
          return revenue;
        });
      });
    },

    initReturnUrl(path) {
      this.returnUrl = `${path}`;
    },
    closeDetailView() {
      if (this.returnUrl) {
        this.$router.push(this.returnUrl);
        return;
      }
      this.$router.push("/invoices/outgoing/");
    },
    exportFile(sendingData, pdf) {
      this.axiosInstance({
        method: "get",
        url: `invoice/${this.companyId}/outgoing/${this.$route.params.invoiceId}/media/${sendingData.id}/?pdf=${pdf}`,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("access"),
        },
        responseType: "arraybuffer",
      }).then((res) => {
        let blob = new Blob([res.data], {
          type: pdf ? "application/pdf" : "text/xml",
        });
        saveAs(
          blob,
          `${
            pdf
              ? sendingData.pdf_file.split("/")[
                  sendingData.pdf_file.split("/").length - 1
                ]
              : sendingData.xml_file.split("/")[
                  sendingData.xml_file.split("/").length - 1
                ]
          }`
        );
      });
    },
    async copyInvoice(revenuesAdded) {
      let data = {
        projectId: this.invoice.project ? this.invoice.project.uuid : null,
        payer: this.invoice.payer ? this.invoice.payer.id : null,
        client: this.invoice.client.id,
      };
      let invoiceData = JSON.parse(JSON.stringify(this.invoice));

      if (revenuesAdded) {
        data.revenues = this.invoiceRevenues;
      } else {
        invoiceData.total_sum = 0;
        invoiceData.tax_sum = 0;
        invoiceData.no_tax_sum = 0;
      }
      data.invoiceData = invoiceData;
      await this.$store.dispatch("invoice/setInvoiceCopyData", data);
      await this.$router.push("/invoices/outgoing/add/");
    },
    createCreditInvoice() {
      let data = {
        projectId: this.invoice.project ? this.invoice.project.uuid : null,
        payer: this.invoice.payer ? this.invoice.payer.id : null,
        client: this.invoice.client.id,
      };
      let invoiceData = JSON.parse(JSON.stringify(this.invoice));
      invoiceData.credit_invoice = true;
      invoiceData.credit_invoice_source = this.invoice.invoice_nr;

      invoiceData.total_sum *= -1;
      invoiceData.tax_sum *= -1;
      invoiceData.no_tax_sum *= -1;

      data.invoiceData = invoiceData;
      this.invoiceRevenues.forEach((x) => (x.amount *= -1));
      data.revenues = this.invoiceRevenues;

      this.$store.dispatch("invoice/setInvoiceCopyData", data);
      this.$router.push("/invoices/outgoing/add/");
    },
  },
  async mounted() {
    if (!this.taxTypes)
      await this.$store.dispatch("companyVariables/retrieveTaxTypes");
    if (!this.currencyList)
      await this.$store.dispatch("companyVariables/retrieveCurrencies");
    if (!this.serviceObjects)
      await this.$store.dispatch("companyVariables/retrieveServiceObjects");
    this.retrieveInvoice();
  },
  beforeRouteEnter(to, from, next) {
    let path = to.query.page ? `${from.path}?page=${to.query.page}` : from.path;
    if (from.name === "ProjectView") {
      path += to.query.page ? "&view=4" : "?view=4";
    } else if (from.name === "OutgoingInvoice") {
      path = `/invoices/outgoing/`;
    }
    return next((vm) => {
      vm.initReturnUrl(path);
    });
  },
  computed: {
    ...mapGetters({
      companyId: "companyData/activeCompanyUuid",
      currencyList: "companyVariables/currencyList",
      serviceObjects: "companyVariables/serviceObjects",
      taxTypes: "companyVariables/taxTypes",
      accountingPermissions: "companyData/userAccountingPermission",
    }),
    currency() {
      if (this.currencyList && this.invoice)
        return this.currencyList.find((x) => x.code === this.invoice.currency);
      return null;
    },
    convertedListSum() {
      if (!this.invoice.no_tax_sum || !this.currencyRate)
        return this.invoice.no_tax_sum;
      return this.convertCurrency(
        parseFloat(this.invoice.no_tax_sum),
        this.currencyRate
      );
    },
    convertedTaxSum() {
      if (!this.invoice.tax_sum || !this.currencyRate)
        return this.invoice.tax_sum;
      return this.convertCurrency(this.invoice.tax_sum, this.currencyRate);
    },
    convertedTotalSum() {
      if (!this.invoice.total_sum || !this.currencyRate)
        return this.invoice.total_sum;
      return this.convertCurrency(this.invoice.total_sum, this.currencyRate);
    },
    invoiceUnpaidAmount() {
      const total = parseFloat(this.invoice.total_sum);
      const paidAmount = parseFloat(this.invoice.paid_amount ?? "0");
      return round2(total - paidAmount);
    },
    convertedUnpaidSum() {
      if (!this.invoice.paid_amount || !this.currencyRate)
        return this.invoiceUnpaidAmount;
      return this.convertCurrency(this.invoiceUnpaidAmount, this.currencyRate);
    },
  },
};
</script>
<style scoped lang="scss">
.file-display {
  @apply shadow  w-full rounded flex duration-100;
  .export-button {
    @apply w-5/6 truncate py-1 px-2 rounded-l cursor-pointer duration-100;
    &:hover {
      @apply bg-primary text-white;
    }
  }
  .delete-button {
    @apply w-1/6 bg-danger rounded-r text-white duration-100 flex items-center justify-center;
    &:hover {
      @apply bg-danger-dark;
    }
  }
  &.read-only {
    .delete-button {
      @apply w-0 hidden;
    }
    .export-button {
      @apply w-full rounded;
    }
  }
}
</style>
